import React from "react";
import type { FieldNode, HubBody, IntroductionBody, OperationBody, WebhookEventsBody } from "../shared/types";
import { CodeBlock, CodeGroup, CopyButton } from "../components/code";
import { FieldList } from "../components/FieldTable";
import { InlineText } from "../components/InlineText";
import { MethodBadge } from "../components/MethodBadge";
import { Markdown } from "../markdown/Markdown";
import { Card, Cards } from "../components/blocks";
import { COPY } from "../copy";

/** API Reference pages (design §4): Introduction, hub overview, operation, webhook events. */

const SectionTitle = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <h2 id={id} className="mt-10 scroll-mt-24 border-b border-border pb-2 text-head4 font-semibold text-foreground">
    {children}
  </h2>
);

const FieldSection = ({ title, fields }: { title: string; fields: FieldNode[] }) =>
  fields.length ? (
    <div className="mt-4">
      <h3 className="text-head6 font-semibold text-foreground">{title}</h3>
      <FieldList fields={fields} />
    </div>
  ) : null;

export const UrlBar = ({ method, url }: { method: OperationBody["method"]; url: string }) => (
  <div className="flex items-center gap-3 overflow-x-auto rounded-lg border border-border bg-card px-3 py-2">
    <MethodBadge method={method} />
    <code className="flex-1 whitespace-nowrap text-bodySmall text-foreground">{url}</code>
    <CopyButton text={url} />
  </div>
);

const CodePanel = ({ body }: { body: OperationBody }) => (
  <div className="space-y-4">
    <CodeGroup samples={body.samples} className="!my-0" />
    <CodeGroup samples={body.responses.flatMap((r) => (r.example ? [{ ...r.example, label: r.status }] : []))} syncLang={false} className="!my-0" />
  </div>
);

export const OperationPage = ({ body }: { body: OperationBody }) => {
  const hasParams = body.params.path.length + body.params.query.length + body.params.header.length > 0;
  const success = body.responses.filter((r) => r.status.startsWith("2"));

  return (
    <div className="clg:grid clg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] clg:gap-10">
      <div className="min-w-0">
        <UrlBar method={body.method} url={body.url} />
        <div className="mt-6 clg:hidden">
          <CodePanel body={body} />
        </div>
        {body.description ? (
          <div className="mt-6">
            <Markdown markdown={body.description} code={[]} />
          </div>
        ) : null}
        <SectionTitle id="authorization">{COPY.authorization}</SectionTitle>
        <p className="mt-3 text-bodySmall text-foreground">
          {body.keyless ? COPY.noKey : <code className="rounded bg-muted px-1 py-0.5">{COPY.keyHeader}</code>}
          {body.scopes.length ? (
            <span className="ml-2 text-muted-foreground">
              {COPY.scope}: {body.scopes.map((s) => s).join(", ")}
            </span>
          ) : null}
        </p>
        {hasParams ? (
          <>
            <SectionTitle id="parameters">{COPY.parameters}</SectionTitle>
            <FieldSection title={COPY.pathParameters} fields={body.params.path} />
            <FieldSection title={COPY.queryParameters} fields={body.params.query} />
            <FieldSection title={COPY.headerParameters} fields={body.params.header} />
          </>
        ) : null}
        {body.body ? (
          <>
            <SectionTitle id="body">{COPY.body}</SectionTitle>
            <p className="mt-2 text-bodySmall text-muted-foreground">
              <code>{body.body.contentType}</code>
              {body.body.description ? (
                <>
                  {" — "}
                  <InlineText text={body.body.description} />
                </>
              ) : null}
            </p>
            <FieldList fields={body.body.fields} />
          </>
        ) : null}
        <SectionTitle id="response">{COPY.response}</SectionTitle>
        {success.map((r) => (
          <div key={r.status} className="mt-4">
            <p className="text-bodySmall text-muted-foreground">
              <span className="font-semibold text-foreground">{r.status}</span> — {r.description}
            </p>
            <FieldList fields={r.fields} />
          </div>
        ))}
        <ul className="mt-4 space-y-1 text-bodySmall text-muted-foreground">
          {body.responses
            .filter((r) => !r.status.startsWith("2"))
            .map((r) => (
              <li key={r.status}>
                <span className="font-semibold text-foreground">{r.status}</span> — {r.description}
              </li>
            ))}
        </ul>
      </div>
      <aside className="hidden clg:block">
        <div className="sticky top-32">
          <CodePanel body={body} />
        </div>
      </aside>
    </div>
  );
};

export const HubPage = ({ body }: { body: HubBody }) => (
  <div>
    <SectionTitle id="available-operations">{COPY.availableOperations}</SectionTitle>
    <Cards>
      {body.operations.map((op) => (
        <Card key={op.path} title={op.label} href={op.path}>
          <span className="mb-1 flex items-center gap-2">
            <MethodBadge method={op.method} />
            <code className="text-headline2">{op.url}</code>
          </span>
          <InlineText text={op.description} />
        </Card>
      ))}
      {body.pages.map((p) => (
        <Card key={p.path} title={p.title} href={p.path}>
          {p.subtitle}
        </Card>
      ))}
    </Cards>
    <div className="my-4 overflow-x-auto">
      <table className="w-full text-left text-bodySmall">
        <thead>
          <tr className="border-b border-border">
            <th className="px-3 py-2">{COPY.operation}</th>
            <th className="px-3 py-2">{COPY.scope}</th>
          </tr>
        </thead>
        <tbody>
          {body.operations.map((op) => (
            <tr key={op.path} className="border-b border-border">
              <td className="px-3 py-2">{op.label}</td>
              <td className="px-3 py-2">{op.keyless ? COPY.noKey : op.scopes.join(", ") || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const IntroductionPage = ({ body }: { body: IntroductionBody }) => (
  <div>
    <p className="text-bodySmall text-muted-foreground">{COPY.baseUrl}</p>
    <div className="mt-1 flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2">
      <code className="flex-1 text-bodySmall text-foreground">{body.baseUrl}</code>
      <CopyButton text={body.baseUrl} />
    </div>
    <SectionTitle id="hubs">{COPY.hubs}</SectionTitle>
    <Cards>
      {body.hubs.map((hub) => (
        <Card key={hub.path} title={hub.title} href={hub.path}>
          {hub.subtitle}
        </Card>
      ))}
    </Cards>
  </div>
);

export const WebhookEventsPage = ({ body }: { body: WebhookEventsBody }) => (
  <div>
    {body.events.map((event) => (
      <section key={event.name}>
        <SectionTitle id={event.anchor}>
          <code>{event.name}</code>
        </SectionTitle>
        <p className="mt-3 text-bodyMedium text-foreground">
          <InlineText text={event.description} />
        </p>
        <FieldList fields={event.fields} />
        <CodeBlock sample={event.example} title={event.name} />
      </section>
    ))}
  </div>
);
